import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Avatar,
  Stack,
  TextField,
  InputAdornment,
  Pagination,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  LocationOn,
  Refresh,
  AccountTree,
  CheckCircle,
  Cancel,
  TrendingUp,
  Hub,
  Photo,
  Visibility,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import odpService from '../services/odpService';
import odcService from '../services/odcService';
import { ODP, ODPStats, ODC } from '../types';
import ODPForm from '../components/ODPForm';
import { useAuth } from '../hooks/useAuth';

const ODPs: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [odps, setODPs] = useState<ODP[]>([]);
  const [odcs, setODCs] = useState<ODC[]>([]);
  const [stats, setStats] = useState<ODPStats>({
    total: 0,
    active: 0,
    inactive: 0,
    byOdc: 0,
    averageRadius: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [odcFilter, setOdcFilter] = useState<string>('');
  const [formOpen, setFormOpen] = useState(false);
  const [selectedODP, setSelectedODP] = useState<ODP | null>(null);
  const [formTitle, setFormTitle] = useState('');

  useEffect(() => {
    fetchODPs();
    fetchStats();
    fetchODCs();
  }, [page, search, statusFilter, odcFilter]);

  const fetchODPs = async () => {
    try {
      setLoading(true);
      setError(null);
      const isActive = statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined;
      console.log('Fetching ODPs with params:', { page, limit: 10, search, isActive, odcFilter });
      const response = await odpService.getODPs(page, 10, search, isActive, undefined, odcFilter || undefined);
      console.log('ODP Response:', response);
      setODPs(response.data.odps);
      setTotalPages(response.data.pagination.totalPages);
    } catch (err: any) {
      console.error('Error fetching ODPs:', err);
      setError(err.response?.data?.message || 'Gagal memuat data ODP');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // For operators: filter stats by their SPOP
      const operatorSpopFilter = user?.role === 'operator' && user?.spop_id ? { spop_id: user.spop_id } : {};
      const response = await odpService.getODPStats(operatorSpopFilter);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchODCs = async () => {
    try {
      console.log('Fetching ODCs for dropdown...');
      // For operators: get ODCs filtered by their SPOP automatically by backend
      const response = await odcService.getODCs({ page: 1, limit: 100 });
      console.log('ODC dropdown response:', response);
      setODCs(response.data.odcs);
    } catch (err) {
      console.error('Error fetching ODCs:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus ODP ini?')) {
      try {
        await odpService.deleteODP(id);
        fetchODPs();
        fetchStats();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Gagal menghapus ODP');
      }
    }
  };

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setPage(1);
    fetchODPs();
  };

  const getODCName = (odcId: string) => {
    const odc = odcs.find(o => o.id === odcId);
    return odc ? odc.fullCode : 'Unknown ODC';
  };

  const handleAdd = () => {
    setSelectedODP(null);
    setFormTitle('Add New ODP');
    setFormOpen(true);
  };

  const handleEdit = (odp: ODP) => {
    setSelectedODP(odp);
    setFormTitle('Edit ODP');
    setFormOpen(true);
  };

  const handleFormSubmit = async (formData: Partial<ODP>, photo?: File | null) => {
    try {
      if (selectedODP) {
        await odpService.updateODP(selectedODP.id, formData, photo || undefined);
      } else {
        await odpService.createODP(formData, photo || undefined);
      }
      fetchODPs();
      fetchStats();
      setFormOpen(false);
    } catch (err: any) {
      throw err; // Let the form handle the error
    }
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedODP(null);
  };

  const handlePhotoView = (photo: string) => {
    const photoUrl = odpService.getPhotoUrl(photo);
    window.open(photoUrl, '_blank');
  };

  if (loading && page === 1) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Data ODP
          </Typography>
          {user?.role === 'operator' && user?.spop && (
            <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
              SPOP: {user.spop.name} ({user.spop.areaCode})
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAdd}
          disabled={user?.role === 'operator'}
        >
          Tambah ODP
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Box sx={{ mb: 3 }}>
          <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Hub color="primary" />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.total}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total ODP
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <CheckCircle color="success" />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.active}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ODP Aktif
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Cancel color="error" />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.inactive}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ODP Tidak Aktif
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <TrendingUp color="info" />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.averageRadius ? Math.round(stats.averageRadius) : 0}m
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Rata-rata Radius
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
            <Box component="form" onSubmit={handleSearch} sx={{ flexGrow: 1 }}>
              <TextField
                fullWidth
                placeholder="Cari ODP berdasarkan kode, alamat..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">Semua</MenuItem>
                <MenuItem value="active">Aktif</MenuItem>
                <MenuItem value="inactive">Tidak Aktif</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>ODC</InputLabel>
              <Select
                value={odcFilter}
                onChange={(e) => setOdcFilter(e.target.value)}
                label="ODC"
              >
                <MenuItem value="">Semua ODC</MenuItem>
                {odcs.map((odc) => (
                  <MenuItem key={odc.id} value={odc.id}>
                    {odc.fullCode}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                setSearch('');
                setStatusFilter('');
                setOdcFilter('');
                setPage(1);
              }}
            >
              Reset
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* ODP Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Full ODP Code</TableCell>
                  <TableCell>ODC</TableCell>
                  <TableCell>Alamat</TableCell>
                  <TableCell>VLAN</TableCell>
                  <TableCell>Radius</TableCell>
                  <TableCell>Lokasi</TableCell>
                  <TableCell>Foto</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Aksi</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {odps.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Tidak ada data ODP
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  odps.map((odp) => (
                    <TableRow key={odp.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {odp.fullCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<AccountTree />}
                          label={getODCName(odp.odcId)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200 }}>
                          {odp.address}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            VLAN {odp.vlanId}
                          </Typography>
                          {odp.vlanName && (
                            <Typography variant="caption" color="text.secondary">
                              {odp.vlanName}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {odp.radius}m
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <LocationOn fontSize="small" color="action" />
                          <Typography variant="caption" fontFamily="monospace">
                            {typeof odp.latitude === 'number' ? odp.latitude.toFixed(6) : parseFloat(odp.latitude).toFixed(6)},
                            {typeof odp.longitude === 'number' ? odp.longitude.toFixed(6) : parseFloat(odp.longitude).toFixed(6)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {odp.photo ? (
                          <IconButton
                            size="small"
                            onClick={() => handlePhotoView(odp.photo!)}
                            color="primary"
                          >
                            <Photo />
                          </IconButton>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No Photo
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={odp.isActive ? 'Aktif' : 'Tidak Aktif'}
                          color={odp.isActive ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(odp)}
                            color="primary"
                            disabled={user?.role === 'operator'}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(odp.id)}
                            color="error"
                            disabled={user?.role === 'operator'}
                          >
                            <Delete />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      <ODPForm
        open={formOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        odp={selectedODP}
        title={formTitle}
      />
    </Box>
  );
};

export default ODPs;
